/**
 * Remote data mode toggle.
 *
 * Checks a small JSON config on R2 CDN to determine if the app should
 * use live DB queries or static JSON caches. This lets us switch modes
 * without redeploying — just update the JSON on R2.
 *
 * Config file: static.americanhealthprices.com/config/data-mode.json
 * Format: { "mode": "db" | "static", "updated": "ISO date" }
 *
 * Priority: ?live URL param > remote config > default (static)
 */

import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

interface DataModeConfig {
  mode: 'db' | 'static';
  updated?: string;
  message?: string;
}

const CONFIG_URL = 'https://static.americanhealthprices.com/config/data-mode.json';

export function useDataMode(): boolean {
  const [searchParams] = useSearchParams();

  // URL param override: ?live forces DB, ?static forces static
  const urlOverride = searchParams.get('live') !== null ? true
    : searchParams.get('static') !== null ? false
    : null;

  const { data: remoteConfig } = useQuery({
    queryKey: ['data-mode-config'],
    queryFn: async (): Promise<DataModeConfig> => {
      try {
        const res = await fetch(CONFIG_URL, { cache: 'no-cache' });
        if (!res.ok) return { mode: 'static' };
        return await res.json();
      } catch {
        return { mode: 'static' };
      }
    },
    staleTime: 2 * 60 * 1000, // Re-check every 2 minutes
    retry: false,
  });

  // Priority: URL param > remote config > default (static)
  if (urlOverride !== null) return urlOverride;
  if (remoteConfig?.mode === 'db') return true;
  return false;
}
